import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { supabase } from "../../supabaseClient";
import { TournamentResults } from "./TournamentResults";
import { useTournamentResultsEnhanced } from "../../hooks/useTournamentResultsEnhanced";
import { useTournamentVerification } from "../../hooks/useTournamentVerification";
import {
  TournamentResultsSkeleton,
  TournamentResultsError,
} from "./components";
import { useTournamentId, useTournamentSlug } from "../../context/TournamentRouteContext";
import TournamentSubpageHead from "../SEO/TournamentSubpageHead";
import { prerenderTournamentForPath } from "../../prerender/pageData";

const TournamentResultsWrapper = () => {
  const tournamentId = useTournamentId();
  const tournamentSlug = useTournamentSlug();
  const location = useLocation();
  const [showPayouts, setShowPayouts] = useState(false);

  // Use the new verification system
  const { verificationStatus, refetch: refetchVerification } =
    useTournamentVerification(tournamentId);

  // Use the new enhanced hook for server-side calculations
  const {
    results,
    payouts,
    showPayouts: serverShowPayouts,
    userPermissions,
    tournament,
    loading,
    isUpdating,
    error,
    updateError,
    refetch,
  } = useTournamentResultsEnhanced(tournamentId, null, showPayouts);

  // Initialize state variables for backward compatibility
  const [organizationName, setOrganizationName] = useState(null);

  // Fetch organization data when we have tournament data
  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (!tournament?.organizationID) return;

      try {
        // Get the organization name
        const { data: organization, error: orgError } = await supabase
          .from("organizations")
          .select("name")
          .eq("id", tournament.organizationID)
          .single();

        if (orgError) {
          console.error("Error fetching organization:", orgError);
          return;
        }

        setOrganizationName(organization.name);
      } catch (error) {
        console.error("Error fetching organization data:", error);
      }
    };

    fetchOrganizationData();
  }, [tournament?.organizationID]);

  const seeded = prerenderTournamentForPath(location.pathname);
  const resultsSeo = (
    <TournamentSubpageHead
      page="standings"
      name={tournament?.name || seeded?.tournament?.name}
    />
  );

  if (loading) {
    return (
      <>
        {resultsSeo}
        <TournamentResultsSkeleton />
      </>
    );
  }

  if (error) {
    return (
      <>
        {resultsSeo}
        <TournamentResultsError
          error={typeof error === "string" ? { message: error } : error}
          onRetry={refetch}
        />
      </>
    );
  }

  if (!results || results.length === 0) {
    return (
      <>
        {resultsSeo}
        <div className="card-board overflow-hidden">
          <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
            <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              Board
            </h1>
            <p className="text-sm text-brand-mist mt-1">
              Places from posted scores.
            </p>
          </div>
          <div className="p-8 text-center">
            <h3 className="heading-display text-lg mb-2">No scores posted yet</h3>
            <p className="text-brand-muted">
              Places show here after teams are entered and scores are recorded.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="mb-8">
      {resultsSeo}
      <TournamentResults
        results={results}
        payouts={payouts}
        showPayouts={serverShowPayouts}
        isPreviewingPayouts={showPayouts}
        setShowPayouts={setShowPayouts}
        userPermissions={userPermissions}
        tournament={tournament}
        organizationName={organizationName}
        tournamentId={tournamentId}
        tournamentSlug={tournamentSlug}
        refetch={refetch}
        verificationStatus={verificationStatus}
        refetchVerification={refetchVerification}
        isUpdating={isUpdating}
        updateError={updateError}
      />
    </div>
  );
};

export default TournamentResultsWrapper;
